import React, { useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { Link } from "react-router-dom";
import { BsArrowLeftSquare, BsArrowRightSquare } from "react-icons/bs";
import { HiOutlineExternalLink } from "react-icons/hi";
import { LuChevronRight, LuFileText, LuX } from "react-icons/lu";
import { SIDEBAR_WIDTHS } from "./sidebarConstants";

export const BaseSidebar = ({
  collapsed,
  toggleCollapse,
  children,
  bottomChildren,
  additionalResources = [],
  logoPath = "/img/logo.png",
  isMobile = false,
  mobileOpen = false,
  closeMobile,
}) => {
  const [showAdditionalResources, setShowAdditionalResources] = useState(false);
  const scrollContainerRef = useRef(null);
  const resourcesButtonRef = useRef(null);
  const resourcesPopoverRef = useRef(null);
  const [showTopShadow, setShowTopShadow] = useState(false);
  const [showBottomShadow, setShowBottomShadow] = useState(false);
  const [resourcesPopoverPosition, setResourcesPopoverPosition] = useState({ top: 0, left: 0 });

  useEffect(() => {
    const container = scrollContainerRef.current;
    if (!container) return undefined;

    const handleScroll = () => {
      const { scrollTop, scrollHeight, clientHeight } = container;
      setShowTopShadow(scrollTop > 0);
      setShowBottomShadow(scrollTop < scrollHeight - clientHeight - 1);
    };

    container.addEventListener("scroll", handleScroll);
    handleScroll();
    return () => container.removeEventListener("scroll", handleScroll);
  }, [collapsed, mobileOpen]);

  useEffect(() => {
    if (collapsed) {
      setShowAdditionalResources(false);
    }
  }, [collapsed]);

  useEffect(() => {
    if (!showAdditionalResources) return undefined;

    const updatePosition = () => {
      if (!resourcesButtonRef.current) return;
      const rect = resourcesButtonRef.current.getBoundingClientRect();
      const popoverHeight = resourcesPopoverRef.current?.offsetHeight || 280;
      const viewportPadding = 12;
      const maxTop = window.innerHeight - popoverHeight - viewportPadding;
      const top = Math.max(viewportPadding, Math.min(rect.top, maxTop));
      setResourcesPopoverPosition({
        top,
        left: rect.right + 10,
      });
    };

    const handleOutsideClick = (event) => {
      if (!resourcesPopoverRef.current || !resourcesButtonRef.current) return;
      const clickedPopover = resourcesPopoverRef.current.contains(event.target);
      const clickedButton = resourcesButtonRef.current.contains(event.target);
      if (!clickedPopover && !clickedButton) {
        setShowAdditionalResources(false);
      }
    };

    const handleEsc = (event) => {
      if (event.key === "Escape") {
        setShowAdditionalResources(false);
      }
    };

    updatePosition();
    window.addEventListener("resize", updatePosition);
    window.addEventListener("scroll", updatePosition, true);
    document.addEventListener("mousedown", handleOutsideClick);
    document.addEventListener("keydown", handleEsc);
    return () => {
      window.removeEventListener("resize", updatePosition);
      window.removeEventListener("scroll", updatePosition, true);
      document.removeEventListener("mousedown", handleOutsideClick);
      document.removeEventListener("keydown", handleEsc);
    };
  }, [showAdditionalResources]);

  const desktopWidth = collapsed ? SIDEBAR_WIDTHS.COLLAPSED : SIDEBAR_WIDTHS.EXPANDED;
  const sidebarWidth = isMobile ? SIDEBAR_WIDTHS.EXPANDED : desktopWidth;

  return (
    <>
      {isMobile && mobileOpen ? (
        <button
          type="button"
          aria-label="Close sidebar backdrop"
          onClick={closeMobile}
          className="fixed inset-0 z-40 bg-black/40"
        />
      ) : null}
      <aside
        className={`fixed top-0 left-0 z-50 h-full border-r-2 border-gray-100 bg-white shadow-sm transition-transform duration-300 ${
          isMobile ? (mobileOpen ? "translate-x-0" : "-translate-x-full") : "translate-x-0"
        }`}
        style={{ width: `${sidebarWidth}px` }}
      >
        <div className="flex h-full flex-col text-gray-800">
          <div className="flex h-[96px] flex-shrink-0 items-center justify-center border-b border-gray-200 bg-white p-4">
            <a
              href="/home"
              className="flex items-center justify-center rounded transition-opacity hover:opacity-80 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2"
            >
              <img
                src={logoPath}
                alt="AMPTRANS logo"
                className="h-auto w-auto object-contain transition-all duration-300"
                style={collapsed && !isMobile ? { maxWidth: 32, maxHeight: 32 } : { maxWidth: 96, maxHeight: 96 }}
              />
            </a>
            {isMobile ? (
              <button
                type="button"
                onClick={closeMobile}
                aria-label="Close sidebar"
                className="absolute right-3 top-3 rounded p-1 text-gray-500 hover:bg-gray-100 hover:text-gray-800"
              >
                <LuX size={20} />
              </button>
            ) : null}
          </div>

          <div className="relative flex-1 overflow-hidden">
            {showTopShadow ? (
              <div className="pointer-events-none absolute left-0 right-0 top-0 z-10 h-8 bg-gradient-to-b from-white to-transparent" />
            ) : null}
            {showBottomShadow ? (
              <div className="pointer-events-none absolute bottom-0 left-0 right-0 z-10 h-8 bg-gradient-to-t from-white to-transparent" />
            ) : null}

            <nav
              ref={scrollContainerRef}
              className="sidebar-scrollbar h-full overflow-y-auto overflow-x-hidden px-2 py-3"
              style={{ scrollbarWidth: "thin", scrollbarColor: "#d1d5db transparent" }}
            >
              <div className="flex min-h-full flex-col">
                <div>{children}</div>
                <div className="flex-1" />
                <div>
                  {bottomChildren ? <div className="pt-4">{bottomChildren}</div> : null}
                  {additionalResources.length > 0 ? (
                    <div className="font-ui">
                      <button
                        ref={resourcesButtonRef}
                        type="button"
                        onClick={() => setShowAdditionalResources((prev) => !prev)}
                        aria-expanded={showAdditionalResources}
                        aria-label={collapsed && !isMobile ? "Additional Resources" : undefined}
                        className={`group relative my-1 flex w-full items-center rounded-lg px-3 py-3 text-sm text-gray-700 transition-all duration-200 hover:bg-gray-100 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 ${
                          collapsed && !isMobile ? "justify-center" : "gap-3"
                        }`}
                      >
                        <div className="flex-shrink-0 text-gray-600">
                          <LuFileText size={20} />
                        </div>
                        {collapsed && !isMobile ? null : (
                          <span className="flex-1 text-left font-ui font-medium">Resources</span>
                        )}
                        {collapsed && !isMobile ? null : (
                          <div className={`transition-transform duration-200 ${showAdditionalResources ? "rotate-90" : ""}`}>
                            <LuChevronRight size={16} />
                          </div>
                        )}
                      </button>
                    </div>
                  ) : null}
                </div>
              </div>
            </nav>
          </div>

          {isMobile ? null : (
            <div className="flex-shrink-0 border-t border-gray-200 bg-white pb-4">
              <div className="p-4">
                <button
                  type="button"
                  onClick={toggleCollapse}
                  aria-label={collapsed ? "Expand sidebar" : "Collapse sidebar"}
                  className="flex w-full items-center justify-center gap-3 rounded-lg py-2 text-gray-500 transition-colors hover:text-gray-800 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2"
                >
                  {collapsed ? <BsArrowRightSquare size={20} /> : <BsArrowLeftSquare size={20} />}
                  {collapsed ? null : <span className="text-sm font-medium">Collapse</span>}
                </button>
              </div>
            </div>
          )}
        </div>
      </aside>
      {showAdditionalResources
        ? createPortal(
            <div
              ref={resourcesPopoverRef}
              className="fixed z-[90] w-72 rounded-xl border border-gray-200 bg-white p-2 shadow-2xl"
              style={{ top: resourcesPopoverPosition.top, left: resourcesPopoverPosition.left }}
            >
              <div className="mb-1 px-2 py-1 text-xs font-semibold uppercase tracking-wider text-gray-400">Resources</div>
              <div className="space-y-1">
                {additionalResources.map((resource, index) => {
                  if (resource.type === "divider") {
                    return <div key={`divider-${index}`} className="my-2 h-px bg-gray-200" />;
                  }

                  const resourceContent = (
                    <>
                      <span>{resource.label}</span>
                      <div className="flex items-center gap-2">
                        {resource.badge ? (
                          <span
                            className={`rounded-full px-1.5 py-0.5 text-[0.625rem] text-white ${
                              resource.badge === "coming-soon" ? "bg-red-500" : "bg-blue-500"
                            }`}
                          >
                            {resource.badge === "coming-soon" ? "Coming Soon!" : resource.badge}
                          </span>
                        ) : null}
                        {resource.external ? (
                          <HiOutlineExternalLink size={14} className="opacity-0 transition-opacity group-hover:opacity-100" />
                        ) : null}
                      </div>
                    </>
                  );

                  if (resource.href) {
                    return (
                      <a
                        key={`${resource.label}-${index}`}
                        href={resource.href}
                        className="group flex items-center justify-between rounded-md p-2 text-sm text-gray-600 transition-colors hover:bg-blue-50 hover:text-blue-600 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2"
                        onClick={() => setShowAdditionalResources(false)}
                      >
                        {resourceContent}
                      </a>
                    );
                  }

                  if (resource.to) {
                    return (
                      <Link
                        key={`${resource.label}-${index}`}
                        to={resource.to}
                        className="group flex items-center justify-between rounded-md p-2 text-sm text-gray-600 transition-colors hover:bg-blue-50 hover:text-blue-600 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2"
                        onClick={() => setShowAdditionalResources(false)}
                      >
                        {resourceContent}
                      </Link>
                    );
                  }

                  return null;
                })}
              </div>
            </div>,
            document.body
          )
        : null}
    </>
  );
};
